import { useNavigation } from 'app/hooks/navigation';
import { isWhiteLabel, FeatureFlag, isFeatureEnabled } from 'app/utils/helpers';
import { WhiteLabel } from 'app/types/white-labels';
import { isValidFestivalDate } from 'app/lib/festival-date';
import { Event, track } from 'app/lib/analytics';
import { useTranslation } from 'react-i18next';
import { addressesLoadedAtom, codeFullWarningAtom, hasProfileAtom, userInfoAtom } from 'app/atoms';
import { useOnboarding } from 'app/hooks/use-onboarding';
import { getI18nLanguage } from 'app/lib/language';
import { storage, StorageKey } from 'app/lib/storage';
import { http } from 'app/lib/axios';
import { addressKeys, userKeys } from 'app/network/keys';
import { type TemporaryAddressDTO, useCreateAddress } from 'app/network/address-mutations';
import { useUpsertOnePreference } from 'app/network/preferences';
import { useAllGroups } from 'app/network/groups';
import { useUpsertMyProfile } from 'app/network/profile';
import type { CreateProfileDTO } from 'app/types/profile';
import type { Address } from 'app/types/address';
import { hasSignal, getSignalData, Signal } from 'app/network/signals';
import type { WarningCodeIsFullData } from 'app/network/signals';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai/index';
import { useState } from 'react';
import type { UseFormHandleSubmit } from 'react-hook-form';

const ACTIVITY_SECTOR_FEATURE = FeatureFlag.useActivitySector;

export function usePersonalInformationSubmitFlow({
  handleSubmit,
}: {
  handleSubmit: UseFormHandleSubmit<CreateProfileDTO>;
}) {
  const navigation = useNavigation();
  const { t: tFestival } = useTranslation('festival');
  const { goToNext } = useOnboarding();
  const i18nLanguage = getI18nLanguage();
  const { mutate: upsertSearchCenterLatitudePreference } = useUpsertOnePreference('searchCenterLatitude');
  const { mutate: upsertSearchCenterLongitudePreference } = useUpsertOnePreference('searchCenterLongitude');
  const { mutate: upsertSearchCenterDistancePreference } = useUpsertOnePreference('searchDistance');
  const { mutate: upsertOnePreference } = useUpsertOnePreference('language');

  const client = useQueryClient();
  const setCodeFullWarning = useSetAtom(codeFullWarningAtom);
  const setAddressesLoaded = useSetAtom(addressesLoadedAtom);
  const setHasProfile = useSetAtom(hasProfileAtom);
  const userInfo = useAtomValue(userInfoAtom);
  const groupsQuery = useAllGroups();
  const activitySectorsEnabled = isFeatureEnabled(ACTIVITY_SECTOR_FEATURE, userInfo?.featureFlags);

  const createAddress = useCreateAddress({
    onSuccess: async (result) => {
      upsertSearchCenterDistancePreference(result.radius);
      if (result.radiusCenterLatitude !== null) upsertSearchCenterLatitudePreference(result.radiusCenterLatitude);
      if (result.radiusCenterLongitude !== null) upsertSearchCenterLongitudePreference(result.radiusCenterLongitude);

      await client.invalidateQueries({ queryKey: addressKeys.list('self') });
      setAddressesLoaded(false);
      client.setQueryData(addressKeys.detail(result.id), result);
      await client.invalidateQueries({ queryKey: userKeys.detail('self') });

      if (hasSignal(result, Signal.WarningCodeIsFull)) {
        const signalData = getSignalData(result, Signal.WarningCodeIsFull) as WarningCodeIsFullData;
        const groupName = signalData.name;
        setCodeFullWarning(groupName);
      }
    },
  });

  const [addressError, setAddressError] = useState<Error | null>(null);

  const { mutate, isPending, error } = useUpsertMyProfile({
    onMutate: () => {
      setAddressError(null);
    },
    onSuccess: async () => {
      try {
        const storedAddress = await storage.readString(StorageKey.TemporaryAddressUsed);
        let parsedAddress: TemporaryAddressDTO | null = null;
        if (storedAddress) {
          parsedAddress = JSON.parse(storedAddress);
        }
        if (parsedAddress) {
          await createAddress.mutateAsync({
            postalCode: parsedAddress.postalCode,
            country: parsedAddress.country,
            radius: parsedAddress.radius,
            latitude: parsedAddress.latitude,
            longitude: parsedAddress.longitude,
            radiusCenterLatitude: parsedAddress.radiusCenterLatitude,
            radiusCenterLongitude: parsedAddress.radiusCenterLongitude,
            city: null,
            administrativeArea: null,
            street: null,
            unit: null,
            name: null,
            icon: null,
          });
        } else {
          // Resumed signups may no longer have the local postal-code draft.
          // Check the server directly: the BFF address cache may still be empty.
          const { data: addresses } = await http.get<Address[]>('/addresses');
          client.setQueryData(addressKeys.list('self'), addresses);
          if (addresses.length === 0) {
            navigation.navigate('onboarding', { screen: 'contact-information', params: { hideCross: true } });
            return;
          }
        }
        await storage.clear(StorageKey.TemporaryFestivalStartDate);
        if (isWhiteLabel(WhiteLabel.Refrain)) track(Event.FestivalDateUpdated, { source: 'signup' });
        setHasProfile(true);
        goToNext();
      } catch (e: unknown) {
        setAddressError(e instanceof Error ? e : new Error(String(e)));
      }
    },
  });

  const onSubmit = handleSubmit(async (form: CreateProfileDTO) => {
    if (isWhiteLabel(WhiteLabel.Refrain)) {
      const date = await storage.readString(StorageKey.TemporaryFestivalStartDate);
      if (!isValidFestivalDate(date ?? undefined)) {
        setAddressError(new Error(tFestival('dateRequired')));
        navigation.navigate('onboarding', { screen: 'contact-information', params: { hideCross: true } });
        return;
      }
      form.festivalStartDate = date!;
    }
    upsertOnePreference(i18nLanguage);
    if (activitySectorsEnabled && form.groupIds === undefined) {
      const userId = userInfo?.id;
      if (userId) {
        form.groupIds =
          groupsQuery.data?.flatMap((group) =>
            group.memberships.some((membership) => membership.user.id === userId) ? [group.id] : [],
          ) ?? [];
      }
    }
    mutate(form);
  });

  const isSubmitLoading = isPending || createAddress.isPending;

  const combinedError = error ?? addressError;

  return { isSubmitLoading, onSubmit, error: combinedError };
}
